import React, { useState, useEffect } from 'react';
import { Button, Col, Row, Form, ListGroup } from 'react-bootstrap';
import { BiArrowBack } from 'react-icons/bi';
import {
	BsFolderFill,
	BsChevronRight,
	BsPlusLg,
	BsXLg,
	BsCheckLg,
} from 'react-icons/bs';
import { RxDividerVertical } from 'react-icons/rx';

import BayModal from '../../BayModal';
import BaySpinner from '../../BaySpinner';
import BayTooltip from '../../BayTooltip';
import {
	regexIsValidPathTest,
	regexHasForbiddenCharTest,
} from '../../utilities/regex';

const regexIsValidPath = new RegExp(regexIsValidPathTest);
const regexHasForbiddenChar = new RegExp(regexHasForbiddenCharTest);

interface FolderItem {
	_id?: string;
	name: string;
	cloudObject?: {
		objKey?: string;
	};
	path?: string;
	depth?: number;
}

interface SelectedRowData {
	_id: string;
	[key: string]: unknown;
}

interface NavigationItem {
	name: string;
	depth: number;
	path: string;
	_id?: string;
}

interface FolderListProps {
	folders: FolderItem[];
	onNavigateEntries: (event: React.MouseEvent, folder: FolderItem) => void;
	selectedItem: FolderItem | null;
	onSelectFolder: (folder: FolderItem) => void;
}

const FolderList: React.FC<FolderListProps> = ({
	folders,
	onNavigateEntries,
	selectedItem,
	onSelectFolder,
}) => {
	return (
		<ListGroup className="entry-folder-list" variant="flush">
			{folders.map((folder, index) => {
				return (
					<ListGroup.Item
						key={index}
						action
						active={folder?._id === selectedItem?._id}
						as="div"
						className="d-flex w-100 justify-between align-items-center cursor-pointer file-content"
						onClick={() => onSelectFolder(folder)}
					>
						<div className="d-flex">
							<span>
								<BsFolderFill color="var(--bs-blue)" size={25} />
							</span>
							<div className="text-truncate ms-2 file-text fs-6">
								<BayTooltip overlay={folder.name} placement="top">
									<span className="mb-1">{folder.name}</span>
								</BayTooltip>
							</div>
						</div>
						<Button
							className="action-button px-1"
							size="sm"
							variant="light"
							onClick={(event) => onNavigateEntries(event, folder)}
						>
							<span>
								<BsChevronRight color="var(--bs-gray-700)" size={20} />
							</span>
						</Button>
					</ListGroup.Item>
				);
			})}
		</ListGroup>
	);
};

interface FolderTitleProps {
	navHistory: NavigationItem[];
}

const FolderTitle: React.FC<FolderTitleProps> = ({ navHistory }) => {
	return navHistory && navHistory.length > 0 ? (
		<span className="text-truncate">
			{navHistory[navHistory.length - 1]?.name}
		</span>
	) : null;
};

interface CurrentEntry {
	name?: string;
	path?: string;
	cloudObject?: {
		objKey?: string;
	};
}

interface MoveEntryModalProps {
	t: (key: string) => string;
	modalType: 'copyTo' | 'moveTo';
	showMoveModal: boolean;
	moveModalSubmitFunc: (item: NavigationItem | FolderItem) => void;
	onCloseFunc: () => void;
	handleCreateFolder: (
		data: { folderName: string; isHidden: boolean; presentOnHomepage: boolean },
		shouldRefresh: boolean
	) => Promise<FolderItem | null>;
	foldersArray: FolderItem[];
	onNavigate: (path: string) => Promise<FolderItem[]>;
	selectedRowData: SelectedRowData[];
	currentEntry?: CurrentEntry;
	isActionLoading?: boolean;
	isReadyToShare?: boolean;
}

// NOTE: While I am using 'folder' in the context of this file,
// it is actually referring to an 'entry' object
// with the type of 'folder' in the database.
// 'folder' and 'entry' are synonymous in this context.
const MoveEntryModal: React.FC<MoveEntryModalProps> = ({
	t,
	modalType,
	showMoveModal,
	moveModalSubmitFunc,
	onCloseFunc,
	handleCreateFolder,
	foldersArray,
	onNavigate,
	selectedRowData,
	currentEntry,
	isActionLoading = false,
	isReadyToShare = false,
}) => {
	const rootPathObject: NavigationItem = {
		name: isReadyToShare ? 'My Ready to Share Content' : 'My Brand Assets',
		depth: 0,
		path: '',
	};

	const [showInputField, setShowInputField] = useState(false);
	const [newFolderName, setNewFolderName] = useState('');
	const [inputError, setInputError] = useState(false);
	const [selectedItem, setSelectedItem] = useState<FolderItem | null>(null);

	const [folderList, setFolderList] = useState<FolderItem[]>(foldersArray ?? []);
	const [navigationHistory, setNavigationHistory] = useState<NavigationItem[]>([rootPathObject]);

	useEffect(() => {
		if (currentEntry) {
			if (
				currentEntry?.name !== 'My Brand Assets' &&
				currentEntry?.name !== 'My Ready to Share Content' &&
				currentEntry?.path !== '/brand-assets' &&
				currentEntry?.path !== '/ready-to-share'
			) {
				const newArray = [...navigationHistory];
				const pathArray = currentEntry?.cloudObject?.objKey
					? currentEntry?.cloudObject?.objKey
					: currentEntry?.path;
				const pathParts = pathArray?.split('/') || [];
				let path = '';
				pathParts.forEach((folder, index) => {
					path = index === 0 ? folder : `${path}/${folder}`;
					const newFolder: NavigationItem = { name: folder, depth: index + 1, path };
					newArray.push(newFolder);
				});
				setNavigationHistory(newArray);
			}
		}

		return () => {
			setShowInputField(false);
			setNewFolderName('');
			setInputError(false);
			setSelectedItem(null);
			setFolderList([]);
			setNavigationHistory([rootPathObject]);
		};
	}, [showMoveModal]);

	const onSelectFolder = (folder: FolderItem) => {
		setSelectedItem(folder);
	};

	const handleInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (
			e.target.value.length > 0 &&
			e.target.value.length <= 64 &&
			regexIsValidPath.test(e.target.value) &&
			!regexHasForbiddenChar.test(e.target.value)
		) {
			setInputError(false);
		} else {
			setInputError(true);
		}

		setNewFolderName(e.target.value);
	};

	const handleCreateFolderEntry = async () => {
		if (
			newFolderName.length <= 0 ||
			!regexIsValidPath.test(newFolderName) ||
			regexHasForbiddenChar.test(newFolderName)
		) {
			setInputError(true);
			return;
		}

		const newPath = navigationHistory[navigationHistory.length - 1]?.path
			? `${navigationHistory[navigationHistory.length - 1]?.path}/${newFolderName}`
			: newFolderName;

		const newFolderObj = await handleCreateFolder(
			{ folderName: newPath, isHidden: true, presentOnHomepage: false },
			false,
		);
		if (newFolderObj) {
			setFolderList(
				[...folderList, newFolderObj].sort((a, b) =>
					a.name.localeCompare(b.name),
				),
			);
			setShowInputField(false);
			setInputError(false);
			setNewFolderName('');
		}
	};

	const submitFunc = () => {
		moveModalSubmitFunc(
			selectedItem || navigationHistory[navigationHistory.length - 1],
		);
	};

	const onNavigateEntries = async (event: React.MouseEvent, folder: FolderItem) => {
		event.preventDefault();
		event.stopPropagation();

		const newArray = [...navigationHistory];
		newArray.push({
			name: folder.name,
			depth: folder?.cloudObject?.objKey?.split('/')?.length || 0,
			path: folder?.cloudObject?.objKey || '',
			_id: folder?._id,
		});
		setNavigationHistory(newArray);

		const returnData = await onNavigate(folder?.cloudObject?.objKey || '');
		setFolderList(returnData);
	};

	const onNavigateBackEntries = async () => {
		// Pop the last folder from the navigation history
		const newArray = [...navigationHistory];
		newArray.length > 1 ? newArray.pop() : [rootPathObject];
		setNavigationHistory(newArray);

		const returnData = await onNavigate(
			newArray[newArray.length - 1]?.name === 'My Brand Assets' ||
				newArray[newArray.length - 1]?.name === 'My Ready to Share Content'
				? ''
				: newArray[newArray.length - 1]?.path,
		);
		setFolderList(returnData);
	};

	const filteredFoldersList = (folderListArray: FolderItem[], selectedRowData: SelectedRowData[]): FolderItem[] => {
		return folderListArray?.filter(
			(e) =>
				!selectedRowData
					?.map((selectedItem) => selectedItem?._id)
					.includes(e._id || ''),
		);
	};

	return (
		<BayModal
			backdrop
			centered
			className="entries-move-modal"
			footer={
				<Row className="w-100 justify-between">
					<Col className="ps-0">
						<Button
							disabled={isActionLoading}
							style={{
								backgroundColor: 'var(--bs-white)',
								color: 'var(--bs-700)',
							}}
							variant="secondary"
							onClick={() => onCloseFunc()}
						>
							{t('cancel')}
						</Button>
					</Col>
					<Col className="pe-0" style={{ textAlign: 'right' }}>
						<Button
							className="ms-3 cancel-upload-delete"
							disabled={isActionLoading || showInputField}
							onClick={() => submitFunc()}
						>
							{modalType === 'copyTo' ? t('copyHere') : t('moveHere')}
						</Button>
					</Col>
				</Row>
			}
			height="100%"
			id="entries_move_modal"
			show={showMoveModal}
			title={
				modalType === 'copyTo' ? t('copyToModalTitle') : t('moveToModalTitle')
			}
		>
			{!showInputField && (
				<div className="d-flex justify-content-between entries-move-header pb-2 mb-3">
					<div className="d-flex align-items-center">
						{navigationHistory.length > 1 && (
							<>
								<Button
									className="d-flex align-items-center bg-white border-0 fw-normal text-primary"
									size="sm"
									variant="light"
									onClick={() => onNavigateBackEntries()}
								>
									<BiArrowBack size={18} />
									<span className="fs-6 ms-1 button-text">{t('back')}</span>
								</Button>
								<div>
									<RxDividerVertical color="var(--bs-gray-700)" size={30} />
								</div>
							</>
						)}
						<div className="h2 mb-0 text-truncate moveto-location">
							<FolderTitle navHistory={navigationHistory} />
						</div>
					</div>
					<Button
						className={`d-flex align-items-center bg-white border-0 fw-normal text-700 ${
							isActionLoading && 'disable-element'
						}`}
						size="sm"
						variant="light"
						onClick={() => setShowInputField(true)}
					>
						<BsPlusLg size={18} />
						<span className="fs-6 ms-1 button-text">{t('newFolder')}</span>
					</Button>
				</div>
			)}
			<div
				className={`border-top pt-2 scrollbar-sm ${
					isActionLoading ? 'bay-placeholder-overlay' : ''
				}`}
				style={{
					minHeight: 235,
					maxHeight: showInputField && inputError ? 325 : 235,
					overflowY: 'auto',
				}}
			>
				{isActionLoading && (
					<BaySpinner
						className="bay-attachment-spinner"
						title={t('pleaseWait')}
					/>
				)}

				{!isActionLoading && showInputField && (
					<Form id="entry_move_form">
						<Form.Group className="my-3">
							<Form.Label className="required">{t('folderName')}</Form.Label>
							<div className="d-flex align-items-center mb-3">
								<Form.Control
									required
									autoComplete="off"
									className={`lh-1 ${!inputError ? '' : 'is-invalid'}`}
									id="edit_entry-input_name"
									isValid={
										newFolderName.length > 0 &&
										newFolderName.length <= 64 &&
										regexIsValidPath.test(newFolderName) &&
										!regexHasForbiddenChar.test(newFolderName)
									}
									maxLength={64}
									placeholder={`${t('enter')} ${t('folderName')}`}
									type="text"
									value={newFolderName}
									onChange={handleInputChanged}
								/>
								<Button
									className={`${inputError ? 'bay-editable-error' : ''} bay-editable-button bay-editable-button-save text-light mx-2 fs-5 py-1 px-2`}
									disabled={
										newFolderName.length === 0 ||
										!regexIsValidPath.test(newFolderName) ||
										regexHasForbiddenChar.test(newFolderName)
									}
									variant="success"
									onClick={handleCreateFolderEntry}
								>
									<BsCheckLg />
								</Button>
								<Button
									className="bay-editable-button bay-editable-button-cancel fs-5 py-1 px-2"
									variant="secondary"
									onClick={() => {
										setShowInputField(false);
										setInputError(false);
									}}
								>
									<BsXLg />
								</Button>
							</div>
							{inputError && (
								<div className="alert alert-danger mt-2" role="alert">
									<h2>{t('invalidFolderName')}</h2>
									<p>{t('invalidFolderNameContent')}</p>
									<ul>
										<li>Must have a minimum of 1 character.</li>
										<li>{'Must not contain + = | * ? / \\ : < > $'}</li>
										<li>Must not start or end with .</li>
										<li>Must not start or end with a space</li>
										<li>Must not contain double spaces</li>
									</ul>
								</div>
							)}
						</Form.Group>
					</Form>
				)}

				{!isActionLoading &&
					filteredFoldersList(folderList, selectedRowData)?.length <= 0 &&
					!showInputField && (
					<p className="py-3">{t('noFolderEntriesPlaceholder')}</p>
				)}

				{!isActionLoading &&
					filteredFoldersList(folderList, selectedRowData)?.length > 0 &&
					!showInputField && (
					<FolderList
						folders={filteredFoldersList(folderList, selectedRowData)}
						selectedItem={selectedItem}
						onNavigateEntries={onNavigateEntries}
						onSelectFolder={onSelectFolder}
					/>
				)}
			</div>
		</BayModal>
	);
};

export default MoveEntryModal;
